import { z } from 'zod';
// --- Source types for provenance attribution ---
export const marketDataSourceTypeSchema = z.enum([
    'api_fred', 'api_rentcast', 'api_hud', 'api_bls',
    'web_search', 'system_default',
]);
// --- ProvenancedValue — every numeric value carries inline provenance ---
export const provenancedValueSchema = z.object({
    value: z.number(),
    unit: z.string(),
    provenance: z.object({
        sourceType: marketDataSourceTypeSchema,
        sourceName: z.string(),
        sourceUrl: z.string().optional(),
        sourceNote: z.string(),
        retrievedAt: z.string(),
        dataAsOf: z.string().optional(),
        confidence: z.enum(['high', 'medium', 'low']),
        stale: z.boolean(),
    }),
});
// --- Per-category data schemas ---
export const rentalRateDataSchema = z.object({
    market_rent_per_sf: provenancedValueSchema.optional(),
    market_rent_per_unit_studio: provenancedValueSchema.optional(),
    market_rent_per_unit_1br: provenancedValueSchema.optional(),
    market_rent_per_unit_2br: provenancedValueSchema.optional(),
    market_rent_per_unit_3br: provenancedValueSchema.optional(),
    rent_growth_annual: provenancedValueSchema.optional(),
    vacancy_rate: provenancedValueSchema.optional(),
});
export const capRateDataSchema = z.object({
    entry_cap_rate: provenancedValueSchema.optional(),
    exit_cap_rate: provenancedValueSchema.optional(),
    cap_rate_trend: z.enum(['compressing', 'stable', 'expanding']).optional(),
    spread_to_10yr_treasury: provenancedValueSchema.optional(),
});
export const constructionCostDataSchema = z.object({
    hard_cost_per_sf: provenancedValueSchema.optional(),
    soft_cost_pct_of_hard: provenancedValueSchema.optional(),
    construction_cost_index: provenancedValueSchema.optional(),
    cost_trend: z.enum(['rising', 'stable', 'declining']).optional(),
});
export const financingTermDataSchema = z.object({
    construction_loan_rate: provenancedValueSchema.optional(),
    permanent_loan_rate: provenancedValueSchema.optional(),
    ltc_typical: provenancedValueSchema.optional(),
    ltv_typical: provenancedValueSchema.optional(),
    dscr_minimum: provenancedValueSchema.optional(),
    treasury_10yr: provenancedValueSchema.optional(),
    sofr_rate: provenancedValueSchema.optional(),
});
export const operatingExpenseDataSchema = z.object({
    property_tax_per_unit: provenancedValueSchema.optional(),
    insurance_per_unit: provenancedValueSchema.optional(),
    management_fee_pct: provenancedValueSchema.optional(),
    total_opex_ratio: provenancedValueSchema.optional(),
});
export const landCostDataSchema = z.object({
    land_cost_per_sf: provenancedValueSchema.optional(),
    land_cost_per_unit: provenancedValueSchema.optional(),
    data_quality: z.enum(['estimate', 'comparable', 'asking_price']).optional(),
});
// --- Data category enum ---
export const marketDataCategorySchema = z.enum([
    'rental_rates', 'cap_rates', 'construction_costs',
    'financing_terms', 'operating_expenses', 'land_costs',
]);
// --- Tool input schema ---
export const marketDataInputSchema = z.object({
    metro: z.string(),
    state: z.string().optional(),
    submarket: z.string().optional(),
    zip_code: z.string().optional(),
    property_type: z.enum([
        'multifamily', 'industrial', 'office', 'retail', 'mixed_use', 'hotel',
    ]).default('multifamily'),
    property_class: z.enum(['A', 'B', 'C']).optional(),
    categories: z.array(marketDataCategorySchema),
    force_refresh: z.boolean().default(false),
});
// --- Tool output schema ---
/**
 * Structured recoverable-error entry (mirrors `toolErrorSchema` in
 * `tool-results.types.ts` — inlined here to avoid a circular import,
 * since `tool-results.types.ts` re-exports `marketDataOutputSchema` as
 * `marketDataEnrichmentResultSchema`). Keep the two schemas in sync.
 */
const marketDataToolErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    inputName: z.string().optional(),
    validInputs: z.array(z.string()).optional(),
    acceptableRange: z.object({ min: z.number(), max: z.number() }).optional(),
    iterations: z.number().int().optional(),
    lastError: z.number().optional(),
    zodIssues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export const marketDataOutputSchema = z.object({
    location: z.object({
        metro: z.string(),
        state: z.string().optional(),
        submarket: z.string().optional(),
        zip_code: z.string().optional(),
        cbsa_code: z.string().optional(),
    }),
    data: z.object({
        rental_rates: rentalRateDataSchema.optional(),
        cap_rates: capRateDataSchema.optional(),
        construction_costs: constructionCostDataSchema.optional(),
        financing_terms: financingTermDataSchema.optional(),
        operating_expenses: operatingExpenseDataSchema.optional(),
        land_costs: landCostDataSchema.optional(),
    }),
    metadata: z.object({
        fetched_at: z.string(),
        cache_hit: z.boolean(),
        total_api_calls: z.number(),
        total_web_searches: z.number(),
        confidence: z.enum(['high', 'medium', 'low']),
        warnings: z.array(z.string()),
        sources_used: z.array(z.string()),
        sources_skipped: z.array(z.object({
            source: z.string(),
            reason: z.string(),
        })),
    }),
    /** Structured recoverable failures — see `toolErrorSchema` in tool-results.types.ts. */
    errors: z.array(marketDataToolErrorSchema).optional(),
});
